<template>
  <div class="play">
    <!-- Async room: who the round is waiting on (src/store/asyncPlay.js). -->
    <p v-if="waitingBanner" class="waiting-banner" :class="{ 'is-mine': waitingOnMe }">{{ waitingBanner }}</p>
    <template v-if="assignment">
      <!--
        No owner's name on the book any more. Bug report (2026-09-11): "I
        think it might be a problem if I can see when my book comes back
        around. Because it will be hard for me to not guess my original
        phrase. Let's lose the names on the books." The reveal still says
        whose book it is - that's the payoff, not a spoiler.
      -->
      <header class="play__head">
        <span class="play__step">Page {{ assignment.step + 1 }} of {{ game.pages }}</span>
        <span class="play__who">{{ bookLabel }}</span>
      </header>

      <!--
        Who's done, visible the whole time. Bug report (2026-09-11): "I don't
        want a timer. But it would be cool to be able to see when everyone
        else is done." One wrapping line of chips; the waiting screen below
        still has the full list once you've sent yours.
      -->
      <ul v-if="!iHaveSubmitted" class="play__status" aria-label="Who has finished this page">
        <li
          v-for="p in game.players"
          :key="p.id"
          :class="{ 'is-done': done(p), 'is-away': !presence[p.id] }"
        >{{ done(p) ? '✓ ' : '' }}{{ p.name }}</li>
      </ul>

      <template v-if="!iHaveSubmitted">
        <!-- Step 0: pick your own word. No cards - Matt: "everyone should just
             get to pick whatever word they want." -->
        <section v-if="assignment.kind === 'word'" class="play__panel">
          <label class="play__label" for="seed">Write anything — a word or a phrase</label>
          <input
            id="seed"
            v-model="text"
            class="play__input"
            maxlength="60"
            autocomplete="off"
            placeholder="a haunted piano"
            @keyup.enter="send"
          />
          <button class="play__suggest" @click="suggest">Suggest one</button>
        </section>

        <section v-else-if="assignment.kind === 'draw'" class="play__panel">
          <p class="play__label">Draw this</p>
          <p class="play__prompt">{{ promptText }}</p>
          <DrawingPad v-model="strokes" />
        </section>

        <section v-else class="play__panel">
          <p class="play__label">What is it?</p>
          <PageView :page="assignment.prompt" fill />
          <input
            v-model="text"
            class="play__input"
            maxlength="60"
            autocomplete="off"
            placeholder="your guess"
            @keyup.enter="send"
          />
        </section>

        <button class="primary play__send" :disabled="!canSend" @click="send">Done</button>
      </template>

      <section v-else class="play__waiting">
        <p class="play__label">Sent. Waiting for the others…</p>
        <ul class="play__list">
          <li v-for="p in game.players" :key="p.id" :class="{ 'is-done': done(p) }">
            <span>{{ p.name }}</span>
            <span>{{ presence[p.id] ? (done(p) ? '✓' : '…') : 'away' }}</span>
          </li>
        </ul>
      </section>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { waitingLine, waitingOnMe as isWaitingOnMe } from '@/store/asyncPlay.js';
import DrawingPad from './DrawingPad.vue';
import PageView from './PageView.vue';
import { hasSubmitted } from '@/engine/rounds';
import { SUGGESTIONS } from '@/engine/suggestions';

export default {
  name: 'PlayScreen',
  components: { DrawingPad, PageView },
  data() { return { text: '', strokes: [] }; },
  computed: {
    waitingOnMe() { return isWaitingOnMe(this.$store.state.roomMeta, this.$store.state.user?.uid); },
    waitingBanner() {
      const meta = this.$store.state.roomMeta;
      const game = this.$store.getters.game;
      if (!meta?.async || !game) return '';
      const nameOf = (id) => (game.players || []).find((p) => p.id === id)?.name || 'someone';
      return waitingLine(meta, this.$store.state.user?.uid, nameOf);
    },
    ...mapState(['presence']),
    ...mapGetters(['game', 'myAssignment', 'iHaveSubmitted']),
    assignment() { return this.myAssignment; },
    promptText() { return this.assignment?.prompt?.content || ''; },
    bookLabel() {
      return this.assignment.kind === 'word' ? 'your book' : 'someone\'s book';
    },
    canSend() {
      if (this.assignment?.kind === 'draw') return this.strokes.length > 0;
      return this.text.trim().length > 0;
    },
  },
  watch: {
    // A new page means a clean pad and an empty box.
    'assignment.step': function reset() { this.text = ''; this.strokes = []; },
  },
  methods: {
    done(player) { return hasSubmitted(this.game, player.seat); },
    suggest() {
      this.text = SUGGESTIONS[Math.floor(Math.random() * SUGGESTIONS.length)];
    },
    send() {
      if (!this.canSend) return;
      this.$store.dispatch('submit', this.assignment.kind === 'draw' ? this.strokes : this.text.trim());
    },
  },
};
</script>

<style scoped>
.waiting-banner { margin: 0; padding: 0.5rem 0.9rem; border-radius: 0.6rem; background: rgba(0,0,0,0.08); font-size: 0.95rem; }
.waiting-banner.is-mine { background: rgba(0,0,0,0.16); font-weight: 700; }
</style>
